import {FixedAspectRatioImageDefinitionDTO, HashDTO, ResolutionDTO, ThumbnailableFileLink} from "src/app/services/api/api";

export interface Clipping {
  clippingTLX: number | null;
  clippingTLY: number | null;
  clippingBRX: number | null;
  clippingBRY: number | null;
}

export interface Coordinate2D {
  x: number;
  y: number;
}

export interface Bounds {
  width: number;
  height: number;
}

export interface Box2D extends Coordinate2D, Bounds {

}

export interface AspectRatioOptions {
  ratio: Bounds;
  maxSize: Bounds;
}

export type Corner = "left" | "topLeft" | "top" | "topRight" | "right" | "bottomRight" | "bottom" | "bottomLeft";

export interface HTMLBounds {
  left?: number;
  top?: number;
  right?: number;
  bottom?: number;
  width?: number;
  height?: number;
}

export class Rect {

  constructor(box?: Box2D) {
    this._x = box && box.x ? box.x : 0;
    this._y = box && box.y ? box.y : 0;
    this._width = box && box.width ? box.width : 0;
    this._height = box && box.height ? box.height : 0;

    this.recalculateCoordinatesFromBox();
  }

  private _x: number;
  get x(): number {
    return this._x;
  }

  set x(value: number) {
    this._x = value;
    this.recalculateCoordinatesFromBox();
  }

  private _y: number;
  get y(): number {
    return this._y;
  }

  set y(value: number) {
    this._y = value;
    this.recalculateCoordinatesFromBox();
  }

  get position(): Coordinate2D {
    return {x: this.x, y: this.y};
  }

  set position(value: Coordinate2D) {
    this._x = value.x;
    this._y = value.y;
    this.recalculateCoordinatesFromBox();
  }

  private _width: number;
  get width(): number {
    return this._width;
  }

  set width(value: number) {
    this._width = value;
    this.recalculateCoordinatesFromBox();
  }

  private _height: number;
  get height(): number {
    return this._height;
  }

  set height(value: number) {
    this._height = value;
    this.recalculateCoordinatesFromBox();
  }

  private _topLeft: Coordinate2D;
  get topLeft(): Coordinate2D {
    return this._topLeft;
  }

  set topLeft(value: Coordinate2D) {
    if (value.x > this._topRight.x) {
      value.x = this._topRight.x;
    }
    if (value.y > this._bottomLeft.y) {
      value.y = this._bottomLeft.y;
    }
    this._topLeft = value;

    this._bottomLeft.x = this._topLeft.x;
    this._topRight.y = this._topLeft.y;

    this.recalculateDimensionsFromPoints();
  }

  private _topRight: Coordinate2D;
  get topRight(): Coordinate2D {
    return this._topRight;
  }

  set topRight(value: Coordinate2D) {
    if (value.x < this._topLeft.x) {
      value.x = this._topLeft.x;
    }
    if (value.y > this._bottomLeft.y) {
      value.y = this._bottomLeft.y;
    }
    this._topRight = value;

    this._bottomRight.x = this._topRight.x;
    this._topLeft.y = this._topRight.y;

    this.recalculateDimensionsFromPoints();
  }

  private _bottomRight: Coordinate2D;
  get bottomRight(): Coordinate2D {
    return this._bottomRight;
  }

  set bottomRight(value: Coordinate2D) {
    if (value.x < this._x) {
      value.x = this._x;
    }
    if (value.y < this._y) {
      value.y = this._y;
    }
    this._bottomRight = value;

    this._topRight.x = this._bottomRight.x;
    this._bottomLeft.y = this._bottomRight.y;

    this.recalculateDimensionsFromPoints();
  }

  private _bottomLeft: Coordinate2D;
  get bottomLeft(): Coordinate2D {
    return this._bottomLeft;
  }

  set bottomLeft(value: Coordinate2D) {
    if (value.x > this._topRight.x) {
      value.x = this._topRight.x;
    }
    if (value.y < this._y) {
      value.y = this._y;
    }
    this._bottomLeft = value;

    this._topLeft.x = this._bottomLeft.x;
    this._bottomRight.y = this._bottomLeft.y;

    this.recalculateDimensionsFromPoints();
  }

  private _forceAspectRatio?: AspectRatioOptions;
  get forceAspectRatio(): AspectRatioOptions {
    return this._forceAspectRatio;
  }

  set forceAspectRatio(value: AspectRatioOptions) {
    this._forceAspectRatio = this.normalizeAspectRatio(value);
    this.recalculateCoordinatesFromBox();
  }

  private normalizeAspectRatio(ratio: AspectRatioOptions): AspectRatioOptions {
    return {ratio: {width: 1, height: ratio.ratio.height / ratio.ratio.width}, maxSize: ratio.maxSize};
  }

  public scale(factor: number): void {
    this.scaleXY(factor, factor);
  }

  public scaleXY(fX: number, fY: number) {
    this._x = Math.round(this._x * fX);
    this._y = Math.round(this._y * fY);
    this._width = Math.round(this._width * fX);
    this._height = Math.round(this._height * fY);

    this.recalculateCoordinatesFromBox();
  }

  private recalculateCoordinatesFromBox(): void {
    this.applyAspectRatio();
    // this.fixAspectRatioMaxBounds();
    this._topLeft = {x: this._x, y: this._y};
    this._topRight = {x: this._x + this._width, y: this._y};
    this._bottomRight = {x: this._x + this._width, y: this._y + this._height};
    this._bottomLeft = {x: this._x, y: this._y + this._height};
  }

  private applyAspectRatio(): void {
    if (this._forceAspectRatio) {
      // if (this._forceAspectRatio.ratio.width === this._forceAspectRatio.ratio.height ||
      //     (!this._forceAspectRatio.ratio.width && !this._forceAspectRatio.ratio.height)) {
      //     if (this._width > this._height) {
      //         this._height = this._width;
      //     }
      //     else {
      //         this._width = this._height;
      //     }
      // }
      // else if (this._forceAspectRatio.ratio.width > this._forceAspectRatio.ratio.height) {
      //     this._height = this._forceAspectRatio.ratio.height * this._width;
      // }
      // else if (this._forceAspectRatio.ratio.width < this._forceAspectRatio.ratio.height) {
      //     this._width = this._forceAspectRatio.ratio.width / this._height;
      // }
    }
  }

  private fixAspectRatioMaxBounds(): void {
    if (this._forceAspectRatio) {
      // if (this._forceAspectRatio?.ratio?.width && this._forceAspectRatio?.ratio?.height &&
      //     this._forceAspectRatio?.maxSize?.width && this._forceAspectRatio?.maxSize?.height) {
      //     if (this._forceAspectRatio.maxSize.width > this._forceAspectRatio.maxSize.height) {
      //         this._width = this._forceAspectRatio.maxSize.width;
      //         this._width = this._height;
      //     }
      //     else {
      //         this._height = this._forceAspectRatio.maxSize.height;
      //         this._height = this._width;
      //     }
      //     if (this._width === this._height) {

      //     }
      //     else if (this._width > this._height) {

      //     }
      //     else if (this._width < this._height) {

      //     }
      // }
    }
  }

  private recalculateDimensionsFromPoints(): void {
    this._x = this._topLeft.x;
    this._y = this._topLeft.y;

    this._width = this._topRight.x - this._topLeft.x;
    this._height = this._bottomLeft.y - this._topLeft.y;
    if (this._forceAspectRatio) {
      this.recalculateCoordinatesFromBox();
    }
  }
}
